<script setup lang="ts">
import {getCurrentInstance, nextTick, onMounted, PropType, ref} from 'vue';
import {CascaderOption, ElMessage} from 'element-plus';
import {CabOrganization, CabTheme, CabUser, OrgThemeCombi, Role, toCab, uuidOf} from 'common';
import {
    createObject,
    deleteObject,
    getCurrentUser,
    listObjects,
    readObject,
    syncUsers,
    updateObject,
} from '@/base/callBackend';
import UserStrip from '@/views/admin/UserStrip.vue';
import PlusButton from '@/views/icons/PlusIcon.vue';
import SyncButton from '@/views/icons/SyncIcon.vue';
import GeneralFilter from '@/views/general/GeneralFilter.vue';
// noinspection ES6UnusedImports - usages in <template> are not properly detected!
import {USER_CHANGE_EMIT, USER_REMOVE_EMIT} from '@/base/cabConstants';

const users           = ref<CabUser[]>([]);
const organizations   = ref<CabOrganization[]>([]);
const filterActive    = ref<boolean>(false);
const filteredUsers   = ref<CabUser[]>([]);
const orgThemeOptions = ref<CascaderOption[]>([]);
const me              = ref<CabUser>();

const props = defineProps({
    selectableRoles: {
        type    : Object as PropType<Role[]>,
        required: false,
    },
});

onMounted(async () => {
    me.value              = toCab(await getCurrentUser());
    users.value           = await listObjects(CabUser);
    users.value           = users.value.filter(user => !user.email || ![me.value!.email!, 'admin@openwalnoot.com'].includes(user.email)); //Never modify yourself or main admin
    organizations.value   = await listObjects(CabOrganization);
    orgThemeOptions.value = await makeCascaderOptions();
});

//=====================================================================================
async function onUserRemove(user: CabUser) {
    await deleteObject(user);
    users.value = users.value.filter((u) => uuidOf(u) !== uuidOf(user));
}

async function onUserAdd() {
    filterActive.value = false;
    const newUser      = new CabUser();
    if (props.selectableRoles) {
        newUser.roles = [props.selectableRoles[0]];
    }
    newUser.organizations = me.value!.organizations;
    newUser.themes        = me.value!.themes;
    await createObject(newUser);
    users.value = [newUser, ... users.value];
    await nextTick();
    getUserStrip(makeStripRef(newUser))?.firstEdit();
}

async function onUserChange(user: CabUser) {
    await updateObject(user);
    users.value = users.value.map((u) =>
        uuidOf(u) === uuidOf(user) ? user : u,
    );

}

const instance = getCurrentInstance();

function getUserStrip(ref: string): InstanceType<typeof UserStrip> | undefined {
    return (instance?.proxy?.$refs[ref] as any)?.[0];
}

function makeStripRef(x: CabUser) {
    return `UserStrip-${uuidOf(x)}`;
}

async function onUserSync() {
    filterActive.value = false;
    await syncUsers();

    // Refresh temp password status for all UserStrip components
    filteredUsers.value.forEach(user => {
        const userStrip = getUserStrip(makeStripRef(user));
        if (userStrip) {
            userStrip.refreshTempPassword();
        }
    });

    ElMessage({
        message: 'Rapporteurs zijn gesynced met de inlog administratie',
        type   : 'success',
    });
}

async function makeCascaderOptions(): Promise<CascaderOption[]> {
    if (me.value!.roles.includes(Role.admin)) {
        return organizations.value.map((o) => ({
            label   : o.name,
            value   : uuidOf(o),
            children: o.themes.map((t) => ({
                label: t.name,
                value: uuidOf(t),
            })),
        }));
    } else {
        // Get allowed org/theme pairs for this user
        const allowedCombis: OrgThemeCombi[] = me.value!.getOrgThemeCombis();

        // Group allowed themes by organization UUID
        const orgMap: Record<string, { org: CabOrganization; themes: CabTheme[] }> = {};
        for (let {
            organization,
            theme
        } of allowedCombis) {
            organization          = await readObject(organization);
            theme                 = await readObject(theme);
            const orgUuid: string = uuidOf(organization);
            if (!orgMap[orgUuid]) {
                orgMap[orgUuid] = {
                    org   : organization,
                    themes: [],
                };
            }
            orgMap[orgUuid].themes.push(theme);
        }

        // Build cascader options
        return Object.values(orgMap).map(({
            org,
            themes,
        }) => ({
            label   : org.name,
            value   : uuidOf(org),
            children: themes.map((t) => ({
                label: t.name,
                value: uuidOf(t),
            })),
        }));
    }
}

//=====================================================================================
</script>

<template>
    <div class="flex flex-col flex-1 mx-28 my-5">
        <div class="flex flex-row flex-1 mb-1">
            <PlusButton @click="onUserAdd()"/>
            <SyncButton @click="onUserSync()"/>
            <div class="text-2xl flex-1 ml-2">
                Gebruikers
            </div>
            <GeneralFilter v-model:active="filterActive"
                           v-model:cabList="users"
                           v-model:cabListFiltered="filteredUsers"
                           :id="'UserList'"/>
        </div>
        <UserStrip
            v-for="user in filteredUsers"
            :key="uuidOf(user)"
            :user="user"
            :allOrganizations="organizations"
            :selectableRoles="props.selectableRoles"
            :orgThemeOptions="orgThemeOptions"
            :me="me!"
            :allUsers="users"
            :ref="makeStripRef(user)"
            v-on:[USER_REMOVE_EMIT]="onUserRemove"
            v-on:[USER_CHANGE_EMIT]="onUserChange"
        />
    </div>
</template>
